import React, { useState } from 'react';
import { useMutation } from '@apollo/client'; 
import { SEND_EMAIL_MUTATION } from '../../graphql/mutations';

/**
 * ContactUsForm Component
 * This component renders a "Contact Us" form and sends an email using the sendEmail mutation.
 * After successful submission, the form is hidden and a confirmation message with an "OK" button is shown.
 *
 * Props:
 * - onClose (required): A callback function that is called when the user clicks "OK" on the confirmation view to close the form.
 */
const ContactUsForm = ({ onClose }) => {
  // Initialize the mutation hook
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('buy'); // Default to "issue"
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  /**
   * validate - Checks for missing or invalid fields.
   * @returns {Object} newErrors - An object containing error messages for invalid fields.
   */
  const validate = () => {
    const newErrors = {};
    if (!name) newErrors.name = 'Name is required';
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!subject) newErrors.subject = 'Subject is required';
    if (!message) newErrors.message = 'Message is required';
    return newErrors;
  };

  /**
   * handleSubmit - Called when the form is submitted.
   * Validates input, constructs the email body, and sends the email using the mutation.
   * @param {Event} e - The form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validate();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    // Construct the text body for the email
    const textBody = `Hello,
You have received a new enquiry from ${name}.
Reason: ${reason === 'issue' ? 'Issue' : 'Purchase Inquiry'}
Subject: ${subject}
Message: ${message}
Best regards,
${name}`;

    // Construct the HTML body for the email
    const htmlBody = `
      <p>Hello,</p>
      <p>You have received a new enquiry from <strong>${name}</strong>.</p>
      <p><strong>Reason:</strong> ${reason === 'issue' ? 'Issue' : 'Purchase Inquiry'}</p>
      <p><strong>Subject:</strong> ${subject}</p>
      <p><strong>Message:</strong><br/>${message}</p>
      <p>Best regards,<br/>${name}</p>
    `;

    try {
      // Send the email using the mutation
      await sendEmail({
        variables: {
          from_email: email, // The sender's email
          to: reason === 'issue' ? 'support@plany365.com' : 'sales@plany365.com',
          subject: `Contact Us Enquiry: ${subject}`,
          text: textBody,
          html: htmlBody,
        },
      });

      // On success, hide the form and show confirmation message
      setSubmitted(true);
      setErrors({});
    } catch (error) {
      console.error('Error sending email:', error);
      // Optionally, set error state to display an error message if needed
    }
  };
 

  // If submitted, show the confirmation view with an "OK" button
  if (submitted) {
    return (
      <div style={{ textAlign: 'center', padding: '1rem' }}>
        <p style={{ color: 'green' }}>
          Thank you for contacting us! We will get back to you shortly.
        </p>
      </div>
    );
  }

  // Render the contact form if not submitted
  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <h2>Contact Us</h2>
      <form onSubmit={handleSubmit}>
        {/* Name Field */}
        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="name">Name:</label>
          <br />
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ width: '100%', padding: '8px' }}
          />
          {errors.name && <p style={{ color: 'red' }}>{errors.name}</p>}
        </div>

        {/* Email Field */}
        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="email">Email:</label>
          <br />
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ width: '100%', padding: '8px' }}
          />
          {errors.email && <p style={{ color: 'red' }}>{errors.email}</p>}
        </div>

        {/* Reason Dropdown */}
        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="reason">Reason for Contact:</label>
          <br />
          <select
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ width: '100%', padding: '8px' }}
          >
            <option value="issue">Issue</option>
            <option value="buy">Purchase Inquiry</option>
          </select>
        </div>

        {/* Subject Field */}
        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="subject">Subject:</label>
          <br />
          <input
            type="text"
            id="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            style={{ width: '100%', padding: '8px' }}
          />
          {errors.subject && <p style={{ color: 'red' }}>{errors.subject}</p>}
        </div>

        {/* Message Field */}
        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="message">Message:</label>
          <br />
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows="5"
            style={{ width: '100%', padding: '8px' }}
          ></textarea>
          {errors.message && <p style={{ color: 'red' }}>{errors.message}</p>}
        </div>

        {/* Submit Button */}
        <button type="submit" style={{ padding: '10px 20px', cursor: 'pointer' }}>
          Submit
        </button>
      </form>
    </div>
  );
};

export default ContactUsForm;
